import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getDispatchers } from '@/api/services/dispatchersService';
import type { Dispatcher } from '@/api/types/Dispatcher';

import type { RootState } from './index';

// Length of the trailing UUID slice used as the fallback label when a sub
// fails to resolve (deactivated dispatcher, since-removed user, audit
// snapshot from a now-gone account). 8 hex chars is enough disambiguation
// for the human eye while staying chip-narrow.
const UUID_FALLBACK_LEN = 8;

export type DispatchersStatus = 'idle' | 'loading' | 'ready' | 'error';

// Trimmed cache shape — only the fields the orders UI actually renders.
// The full Dispatcher (with phoneNumber / state) is fetched but we don't
// persist it; the resolver only needs displayName.
export interface DispatcherCacheEntry {
  sub: string;
  displayName: string;
}

export interface DispatchersState {
  byId: Record<string, DispatcherCacheEntry>;
  status: DispatchersStatus;
  fetchedAt: number | null;
}

const initialState: DispatchersState = {
  byId: {},
  status: 'idle',
  fetchedAt: null,
};

// Boot-time fetch dispatched from AppLayout once the user is authenticated.
// No rejectWithValue plumbing — failures degrade gracefully (the resolver
// falls back to `…last8` per the selector contract); the toast handler in
// the axios interceptor already surfaces the error to the user.
export const loadDispatchers = createAsyncThunk<Dispatcher[]>(
  'dispatchers/load',
  async () => getDispatchers(),
);

const dispatchersSlice = createSlice({
  name: 'dispatchers',
  initialState,
  reducers: {
    // Marks the cache as stale without clearing entries — callers that
    // mutate the KC user set (admin user-management) should dispatch this so
    // the next `loadDispatchers()` refetches. Keeping `byId` populated avoids
    // a flash of UUID fallbacks while the refetch is in flight.
    invalidate(state) {
      state.status = 'idle';
      state.fetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDispatchers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadDispatchers.fulfilled, (state, action) => {
        state.byId = Object.fromEntries(
          action.payload.map((d) => [d.sub, { sub: d.sub, displayName: d.displayName }]),
        );
        state.status = 'ready';
        state.fetchedAt = Date.now();
      })
      .addCase(loadDispatchers.rejected, (state) => {
        state.status = 'error';
      });
  },
});

export const { invalidate: invalidateDispatchers } = dispatchersSlice.actions;
export const dispatchersReducer = dispatchersSlice.reducer;

// Resolver selector — accepts a possibly-null sub (matches the OrderResponse
// `assignedDispatcherSub: string | null` shape).
//   - null sub → '' (caller is expected to render the unassigned chip instead)
//   - known sub → display name (e.g. "Petr Vala")
//   - unknown sub → `…<last8>` ellipsis fallback (deactivated dispatcher,
//     historical audit snapshot from a sub that's no longer in the live list)
export const selectDispatcherName =
  (sub: string | null) =>
  (state: RootState): string => {
    if (sub === null) return '';
    const known = state.dispatchers.byId[sub];
    if (known) return known.displayName;
    return `…${sub.slice(-UUID_FALLBACK_LEN)}`;
  };
